import {isEmpty, isNil, map, transform} from 'lodash';
import {Fragment, useCallback, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {InformationModal, Icon, CopyText, Divider, IconName, IconSize, Tooltip} from 'components';
import {useUserInfoSchema} from 'hooks';
import {AgentSession, AnyParticipant, ClientDevice, OnlineSession, SessionAppointment} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';
import {InviteeParticipantRole, ParticipantRole} from 'types';
import {sessionUtils, userInfoUtils} from 'utils';

const T_KEYS = TRANSLATION_KEYS.COMPONENTS.PARTICIPANT_DETAILS_MODAL;
const T_KEYS_LABELS = TRANSLATION_KEYS.COMMON.SESSION.LABELS;

type Props = {
    open: boolean;
    participant?: AnyParticipant;
    session?: AgentSession | OnlineSession | SessionAppointment;
    showJoinLink?: boolean;
    guestEnabled?: boolean;
    onSendInvite?: (participant: AnyParticipant) => void;
    onClose: () => void;
}

export const ParticipantDetailsModal: React.FC<Props> = ({
    open, session, participant, guestEnabled, showJoinLink, onClose, onSendInvite
}: Props) => {
    const {t} = useTranslation();

    const userInfoRole: InviteeParticipantRole = useMemo(() => {
        if (participant?.role === ParticipantRole.Guest) {
            return InviteeParticipantRole.Guest;
        }

        if (participant?.role === ParticipantRole.Client) {
            return InviteeParticipantRole.Client;
        }

        return null;
    }, [participant]);

    const {getUserInfoFields, getControlOnlyFields} = useUserInfoSchema({fetchOnInit: false, role: userInfoRole, guestEnabled});

    const [userInfoFields, controlOnlyFields] = useMemo(() => [
        getUserInfoFields?.({groupId: session?.assignment?.groupId}),
        getControlOnlyFields?.({groupId: session?.assignment?.groupId})
    ], [getUserInfoFields, getControlOnlyFields, session]);

    // We cannot use ParticipantName component since we need custom styles
    const participantName = useMemo(() => sessionUtils.isUserParticipant(participant) ?
        sessionUtils.getParticipantName(participant?.data) :
        sessionUtils.getParticipantName(participant?.data?.userInfo),
    [participant]);

    const participantUserInfoFields = useMemo(() => transform(userInfoFields, (acc, field) => {
        if (['firstName', 'lastName', 'id', 'email', 'phone'].includes(field.name)) {
            return;
        }
        const value: string = participant?.data?.userInfo?.[field.name];
        if (value) {
            acc.push({
                key: field.name,
                label: userInfoUtils.getUserInfoFieldLabel(field, t),
                value
            });
        }
    }, []), [participant, userInfoFields, t]);

    const participantControlOnlyFields = useMemo(() => transform(controlOnlyFields, (acc, field) => {
        const value: string = participant?.data?.userInfo?.controlOnlyInfo?.[field.name];
        if (value) {
            acc.push({
                key: field.name,
                label: userInfoUtils.getUserInfoFieldLabel(field, t),
                value
            });
        }
    }, []), [participant, controlOnlyFields, t]);

    const renderKeyValueLine = useCallback((label: string, value: string) => (
        <Fragment>
            <div className='tw-table-cell tw-text-default tw-min-w-24 tw-max-w-52 tw-mr-2 tw-py-1 tw-pr-3 tw-text-break-word'>{label}</div>
            <CopyText className='tw-table-cell' copiedLabelOnly textInTooltip={false} iconSize='sm' text={value} labelPlacement='left' />
        </Fragment>
    ), []);

    const renderIconValueLine = useCallback((icon: IconName, value: string, iconSize: IconSize = 'md') => (
        !isNil(value) && value !== '' ?
            <div className='tw-flex tw-my-2 tw-text-lg tw-items-center'>
                <Icon name={icon} size={iconSize} className='tw-mr-3' />
                <CopyText
                    className='tw-table-cell tw-break-all'
                    copiedLabelOnly
                    textInTooltip={false}
                    iconSize='sm'
                    text={value}
                    labelPlacement='left'
                />
            </div> : null
    ), []);

    return (
        <InformationModal open={open} title={t(T_KEYS.TITLE)} fullWidth maxWidth='sm' onClose={onClose}>
            {participant?.role === ParticipantRole.Assistant &&
                <Fragment>
                    {showJoinLink && session && !participant.removed &&
                        <div className='tw-mb-4 tw-flex tw-items-center'>
                            {renderIconValueLine('external-link', sessionUtils.getParticipantJoinSessionLink(session as any, participant))}
                        </div>}
                    {renderIconValueLine('assistant-participant', participantName)}
                    {renderIconValueLine('mail', participant.data?.email)}
                </Fragment>}
            {sessionUtils.isInviteeParticipant(participant) &&
                <Fragment>
                    {showJoinLink && session && !participant.removed &&
                    (!participant.data?.device || participant.data?.device === ClientDevice.Browser) &&
                        <div className='tw-mb-4 tw-flex tw-items-center'>
                            {renderIconValueLine('external-link', sessionUtils.getParticipantJoinSessionLink(session as any, participant))}
                            {onSendInvite &&
                                <Tooltip message={t(T_KEYS.SEND_INVITE)} className='tw-min-w-12 tw-text-right tw-ml-auto'>
                                    <Icon
                                        button
                                        className='tw-mx-2'
                                        name='reply'
                                        color='primary'
                                        onClick={() => onSendInvite(participant)}
                                    />
                                </Tooltip>}
                        </div>}
                    {renderIconValueLine('id', participant.data?.userInfo?.id)}
                    {participant?.role === ParticipantRole.Client && renderIconValueLine('client-participant', participantName)}
                    {participant?.role === ParticipantRole.Guest && renderIconValueLine('guest-participant', participantName)}
                    {renderIconValueLine('mail', participant.data?.userInfo.email)}
                    {renderIconValueLine('phone', participant.data?.userInfo.phone)}
                    {renderIconValueLine('map-pin', participant.data?.location)}
                    {(!isEmpty(participantUserInfoFields) || !isEmpty(participantControlOnlyFields)) && <Divider className='tw-my-4' />}
                    {!isEmpty(participantUserInfoFields) &&
                        <div className='tw-table tw-text-lg tw-break-all'>
                            {map(participantUserInfoFields, ({key, label, value}) => (
                                <div key={key} className='tw-table-row'>
                                    {renderKeyValueLine(label, value)}
                                </div>
                            ))}
                        </div>}
                    {!isEmpty(participantControlOnlyFields) &&
                        <Fragment>
                            <div className='tw-mt-4 tw-mb-2 tw-font-bold tw-text-lg'>{t(T_KEYS_LABELS.CONTROL_ONLY_INFO)}</div>
                            <div className='tw-table tw-text-lg tw-break-all'>
                                {map(participantControlOnlyFields, ({key, label, value}) => (
                                    <div key={key} className='tw-table-row'>
                                        {renderKeyValueLine(label, value)}
                                    </div>
                                ))}
                            </div>
                        </Fragment>}
                    {participant.data.meta?.urlFrom &&
                        <Fragment>
                            <Divider className='tw-my-4' />
                            <div className='tw-flex tw-my-2 tw-text-lg tw-items-center tw-break-all'>
                                <Icon name='paper-clip' className='tw-mr-1' />
                                <CopyText
                                    text={participant.data.meta?.urlFrom}
                                    iconSize='sm'
                                    className='tw-ml-2'
                                    labelPlacement='left'
                                    textInTooltip={false}
                                    label={<span className='tw-line-clamp-3'>{participant.data.meta?.urlFrom}</span>}
                                />
                            </div>
                        </Fragment>}
                </Fragment>}
        </InformationModal>
    );
};
